import { useTraduccion } from '@tallerya/i18n';
import {
  Boton,
  CampoCasillas,
  CampoCorreo,
  CampoEtiquetas,
  CampoTelefono,
  CampoTexto,
  Fila,
  Formulario,
  Seccion,
  useAvisos,
  useFormulario,
  type EstadoFormulario,
} from '@tallerya/ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { EliminarPersona } from './EliminarPersona';
import {
  nombreDe,
  pedirSiguienteCodigo,
  useCambiosPersona,
  useEspecialidades,
  useFichaPersona,
  useSiguienteCodigo,
  useSucursalesDelTaller,
  version,
  type FichaPersona,
  type ValoresPersona,
} from './datos';

// Alta y edición de una persona del taller (#58): «Mecánicos/Operadores - Nuevo
// mecánico» y «Editar mecánico» del Figma. La misma pantalla para las dos, como
// en proveedores:
//
//   /personal/nuevo        alta: el código llega ya propuesto
//   /personal/:id/editar   edición: se pide la ficha, con la versión para guardar
//
// Lo que no está, y por qué:
//
// - **«Acceso al sistema»**: rol, PIN y QR son de #208 (fase 4).
// - **El código en la edición**: es el usuario para entrar, y se cambia con su
//   ventana desde la ficha (`CambiarCodigoPersona`). En el alta sí se escribe.
// - **«Estado» con el interruptor**: como en proveedores, se activa y desactiva
//   desde el listado y la ficha. Toda alta nace activa.
//
// Solo el nombre y el apellido son obligatorios: lo demás se completa después.

/** Los topes de la API (`Staff`), para el contador de los campos (#176). */
const MAXIMO = {
  /** `Staff.EmployeeCodeMaxLength` */
  codigo: 50,
  /** `Staff.NameMaxLength` */
  nombre: 250,
  /** `Staff.PhoneMaxLength` */
  telefono: 50,
  /** `Staff.EmailMaxLength` */
  correo: 254,
};

/** El alta en blanco. El código lo pone la propuesta de `next-code`. */
const ALTA: ValoresPersona = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  hiredOn: '',
  phone: '',
  email: '',
  specialties: [],
  locations: [],
};

export function FormularioPersona() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const alta = id === undefined;
  const ficha = useFichaPersona(id);
  const siguiente = useSiguienteCodigo(alta);
  useTituloPantalla(alta ? t('admin.personal.nuevo') : t('admin.personal.editar.titulo'));

  if (alta) {
    // Se espera la propuesta antes de dibujar, para que el formulario arranque con
    // ella. Si falla, el alta sigue con el código vacío: no se bloquea por eso.
    if (siguiente.isPending) return <p className="entrada">{t('app.cargando')}</p>;

    return <Editor key="nuevo" codigoPropuesto={siguiente.data ?? ''} />;
  }

  if (ficha.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (ficha.isError || ficha.data === null) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.personal.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.personal.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/personal')}>
          {t('admin.personal.volver')}
        </Boton>
      </section>
    );
  }

  // La clave lleva la versión: si la ficha cambia en la base y se vuelve a pedir,
  // el formulario arranca de nuevo con los valores frescos.
  return <Editor key={`${String(ficha.data.id)}-${String(ficha.data.version)}`} ficha={ficha.data} />;
}

interface PropsEditor {
  ficha?: FichaPersona;
  /** Solo el alta: el código que tendría la próxima persona, o vacío si no se pudo saber. */
  codigoPropuesto?: string;
}

function Editor({ ficha, codigoPropuesto = '' }: PropsEditor) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosPersona();
  const especialidades = useEspecialidades();
  const sucursales = useSucursalesDelTaller();
  const [eliminando, setEliminando] = useState(false);
  // El código que se mostró ya puesto. Si al guardar sigue igual, nadie lo tocó.
  const [propuesto, setPropuesto] = useState(codigoPropuesto);
  // El que asignó la API, para el aviso: puede no ser el propuesto.
  const asignado = useRef('');
  const editando = ficha !== undefined;
  // Con algo detrás —la ficha o el listado con sus filtros— se vuelve con
  // «Atrás». Quien entró por un enlace va al listado limpio.
  const origen = useLocation().state as { desdeFicha?: boolean; desdeListado?: boolean } | null;
  const salir = () => void (origen?.desdeFicha || origen?.desdeListado ? navegar(-1) : navegar('/personal'));
  // Eliminada, su ficha ya no existe: se salta hasta lo que había detrás de ella,
  // o se reemplaza esta pantalla por el listado limpio.
  const alEliminar = () =>
    void (origen?.desdeFicha
      ? origen.desdeListado
        ? navegar(-2)
        : navegar('/personal', { replace: true })
      : salir());

  const form = useFormulario<ValoresPersona>({
    inicial: ficha
      ? {
          employeeCode: ficha.employeeCode,
          firstName: ficha.firstName,
          lastName: ficha.lastName,
          hiredOn: ficha.hiredOn ?? '',
          phone: ficha.phone ?? '',
          email: ficha.email ?? '',
          specialties: ficha.specialties,
          locations: ficha.locations.map(String),
        }
      : { ...ALTA, employeeCode: codigoPropuesto },
    guardar: async (v) => {
      if (ficha) return cambios.editar(String(ficha.id), version(ficha.version), v);
      const { codigo, problema } = await cambios.crear(v, propuesto);
      asignado.current = codigo ?? '';
      return problema;
    },
    camposPorCodigo: {
      'staff.codigo_tomado': 'employeeCode',
      'staff.sucursal_no_encontrada': 'locations',
    },
    // Si otra persona guardó mientras tanto, «Recargar» trae la ficha nueva: su
    // versión cambia la clave del editor y el formulario arranca con lo fresco.
    recargar: () => void cambios.refrescar(),
  });

  // Si la persona escribe su código mientras llega el propuesto de «Guardar y
  // agregar otro», manda el suyo. Como en `FormularioProveedor`.
  const codigoTocado = useRef(false);
  const formCodigo: EstadoFormulario<ValoresPersona> = {
    ...form,
    cambiar: (campo, valor) => {
      if (campo === 'employeeCode') codigoTocado.current = true;
      form.cambiar(campo, valor);
    },
  };

  // Las sucursales activas se ofrecen; una inactiva que ya tenía se sigue
  // mostrando marcada, para no quitársela sin que nadie lo decida.
  const opcionesSucursal = (sucursales.data ?? [])
    .filter((s) => s.isActive || form.valores.locations.includes(String(s.id)))
    .map((s) => ({ valor: String(s.id), etiqueta: s.name }));
  // Un taller de una sola sucursal no tiene nada que asignar: el campo sobra,
  // salvo para quitar la que ya tenga. Sin la lista —sin `settings.edit`— tampoco
  // se muestra, y lo asignado viaja como estaba.
  const conSucursales =
    opcionesSucursal.length > 1 || (opcionesSucursal.length === 1 && form.valores.locations.length > 0);

  async function guardado(siguiente: 'volver' | 'otro') {
    const nombre = nombreDe(form.valores);
    avisar({
      titulo: editando
        ? t('admin.personal.aviso.guardado', { nombre })
        : t('admin.personal.aviso.creado', { nombre, codigo: asignado.current }),
    });
    if (siguiente === 'otro') {
      // Se limpia antes de pedir el código: con los datos del recién creado a la
      // vista, un segundo «Guardar» en la espera daría de alta un duplicado.
      codigoTocado.current = false;
      form.reiniciar(ALTA);
      setPropuesto('');
      requestAnimationFrame(() => document.getElementById(form.idDe('firstName'))?.focus());
      const codigo = await pedirSiguienteCodigo();
      if (codigoTocado.current) return;
      setPropuesto(codigo);
      form.cambiar('employeeCode', codigo);
      return;
    }
    salir();
  }

  return (
    <>
      <Formulario
        form={form}
        titulo={editando ? t('admin.personal.editar.titulo') : t('admin.personal.nuevo')}
        info={editando ? t('admin.personal.editar.info') : t('admin.personal.nuevo.info')}
        // Al editar, de quién se trata: «EMP-0001 · Carlos Méndez · Activo».
        {...(ficha && {
          descripcion: [
            ficha.employeeCode,
            nombreDe(ficha),
            ficha.isActive ? t('admin.personal.activo') : t('admin.personal.inactivo'),
          ].join(' · '),
        })}
        {...(editando && { etiquetaGuardar: t('admin.personal.formulario.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={(siguiente) => void guardado(siguiente)}
        onCancelar={salir}
        {...(ficha && {
          accionSecundaria: (
            <Boton variante="peligro" onClick={() => setEliminando(true)}>
              {t('admin.personal.formulario.eliminar')}
            </Boton>
          ),
        })}
      >
        <Seccion titulo={t('admin.personal.formulario.datos')} info={t('admin.personal.formulario.datosInfo')}>
          <Fila>
            <CampoTexto
              form={form}
              nombre="firstName"
              etiqueta={t('admin.personal.formulario.nombre')}
              obligatorio
              placeholder={t('admin.personal.formulario.nombreEjemplo')}
              maximo={MAXIMO.nombre}
              autoComplete="off"
            />
            <CampoTexto
              form={form}
              nombre="lastName"
              etiqueta={t('admin.personal.formulario.apellido')}
              obligatorio
              placeholder={t('admin.personal.formulario.apellidoEjemplo')}
              maximo={MAXIMO.nombre}
              autoComplete="off"
            />
            {!editando && (
              <CampoTexto
                form={formCodigo}
                nombre="employeeCode"
                etiqueta={t('admin.personal.formulario.codigo')}
                // Vacío vale: la API asigna el código.
                info={t('admin.personal.formulario.codigoAyuda')}
                maximo={MAXIMO.codigo}
                autoComplete="off"
              />
            )}
            <CampoTexto form={form} nombre="hiredOn" etiqueta={t('admin.personal.formulario.ingreso')} tipo="date" />
          </Fila>
          <Fila>
            <CampoTelefono
              form={form}
              nombre="phone"
              etiqueta={t('admin.personal.formulario.telefono')}
              maximo={MAXIMO.telefono}
            />
            <CampoCorreo
              form={form}
              nombre="email"
              etiqueta={t('admin.personal.formulario.correo')}
              info={t('admin.personal.formulario.correoAyuda')}
              maximo={MAXIMO.correo}
              ancho="largo"
            />
          </Fila>
        </Seccion>

        <Seccion titulo={t('admin.personal.formulario.trabajo')} info={t('admin.personal.formulario.trabajoInfo')}>
          <CampoEtiquetas
            form={form}
            nombre="specialties"
            etiqueta={t('admin.personal.formulario.especialidades')}
            info={t('admin.personal.formulario.especialidadesAyuda')}
            placeholder={t('admin.personal.formulario.especialidadesPlaceholder')}
            etiquetaQuitar={t('admin.personal.formulario.quitar')}
            sugerencias={(especialidades.data ?? []).map((e) => e.name)}
            ancho="largo"
          />
          {conSucursales && (
            <CampoCasillas
              form={form}
              nombre="locations"
              etiqueta={t('admin.personal.formulario.sucursales')}
              info={t('admin.personal.formulario.sucursalesAyuda')}
              opciones={opcionesSucursal}
              ancho="completo"
            />
          )}
        </Seccion>
      </Formulario>

      <EliminarPersona
        persona={eliminando ? (ficha ?? null) : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
      />
    </>
  );
}
